import { isPlainObject } from 'is-plain-object';
import * as Y from 'yjs';

export type YToJSON<T> =
    T extends Y.Array<infer Item>
        ? YToJSON<Item>[]
        : T extends Y.Map<infer V>
          ? { [key: string]: YToJSON<V> }
          : T extends string | boolean | null | undefined | number
            ? T
            : any;

/**
 * Convert any Yjs data structure to its JSON equivalent.
 */
export function yToJSON<Value>(input: Value): YToJSON<Value> {
    // JavaScript primitives
    if (
        typeof input === 'string' ||
        typeof input === 'number' ||
        typeof input === 'boolean' ||
        input === null ||
        input === undefined
    ) {
        // @ts-ignore
        return input;
    }

    // Yjs data structures
    if (input instanceof Y.Array) {
        // @ts-ignore
        return yArrayToJSON(input);
    }
    if (input instanceof Y.Map) {
        // @ts-ignore
        return yMapToJSON(input);
    }

    // JS data structures
    if (isPlainObject(input) || isJSONRecord(input)) {
        const result: { [key: string]: YToJSON<any> } = {};

        // @ts-ignore
        for (const [key, value] of Object.entries(input)) {
            // @ts-ignore
            result[key] = yToJSON(value);
        }

        // @ts-ignore
        return result;
    }
    if (Array.isArray(input)) {
        // @ts-ignore
        return input.map((item) => yToJSON(item));
    }

    throw new Error(`Unknown Yjs type: ${typeof input}`);
}

function yArrayToJSON<T>(array: Y.Array<T>): YToJSON<T>[] {
    return array.toArray().map((item) => yToJSON(item));
}

function yMapToJSON<T>(map: Y.Map<T>): { [key: string]: YToJSON<T> } {
    const result: { [key: string]: YToJSON<T> } = {};

    map.forEach((value, key) => {
        result[key] = yToJSON(value);
    });

    return result;
}

/**
 * `isPlainObject` rejects a map with an own `constructor` key, such as tags keyed by the slug
 * "constructor".
 */
function isJSONRecord(value: unknown): value is Record<string, unknown> {
    if (typeof value !== 'object' || value === null) return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}
